import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StatusBadge from '../../components/shared/StatusBadge';
import Badge from '../../components/ui/Badge';
import { formatDateTime } from '../../utils/formatDate';
import toast from 'react-hot-toast';

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadTicket = async () => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setTicket(data.ticket);
    } catch (err) {
      console.error('Load ticket error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTicket(); }, [id]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.post(`/tickets/${id}/messages`, { message });
      setMessage('');
      loadTicket();
      toast.success('Reply sent');
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return <div className="text-center text-slate-400 py-12">Ticket not found</div>;
  }

  return (
    <div>
      <Link to="/central/support" className="text-sm text-slate-400 hover:text-cyan-400 mb-4 inline-block">&larr; Back to Tickets</Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-sm font-mono text-cyan-400">{ticket.ticketNumber}</span>
            <StatusBadge status={ticket.status} />
            <Badge color={ticket.priority === 'critical' ? 'red' : ticket.priority === 'high' ? 'yellow' : 'gray'}>
              {ticket.priority}
            </Badge>
          </div>
          <h1 className="text-xl font-bold text-slate-100">{ticket.subject}</h1>
          <p className="text-sm text-slate-400 mt-1">
            Created by {ticket.createdBy?.name} &middot; {formatDateTime(ticket.createdAt)}
            {ticket.appliance && <> &middot; {ticket.appliance.unitName}</>}
          </p>
        </div>
      </div>

      {/* Description */}
      <Card className="mb-6">
        <p className="text-sm text-slate-300 whitespace-pre-wrap">{ticket.description}</p>
      </Card>

      {/* Messages */}
      <div className="space-y-4 mb-6">
        {ticket.messages?.map(msg => {
          const isOwn = msg.senderId === user?.id;
          const isStaff = msg.sender?.role === 'admin' || msg.sender?.role === 'tech_support';
          return (
            <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[70%] rounded-xl p-4 ${
                isOwn ? 'bg-cyan-500/10 border border-cyan-500/20' :
                isStaff ? 'bg-navy-700 border border-navy-600' :
                'bg-navy-800 border border-navy-700'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-slate-200">{msg.sender?.name}</span>
                  {isStaff && <Badge color="cyan">Staff</Badge>}
                  <span className="text-xs text-slate-500">{formatDateTime(msg.createdAt)}</span>
                </div>
                <p className="text-sm text-slate-300 whitespace-pre-wrap">{msg.message}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply form */}
      {ticket.status !== 'closed' && (
        <Card>
          <form onSubmit={handleSend}>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your reply..."
              rows={3}
              className="w-full px-4 py-3 bg-navy-900 border border-navy-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 resize-y"
            />
            <div className="flex justify-end mt-3">
              <Button type="submit" disabled={sending || !message.trim()}>
                {sending ? 'Sending...' : 'Send Reply'}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
